import axios from 'axios';
import Cookies from 'universal-cookie';
import { jwtDecode } from 'jwt-decode';
import { getCookieNameSuffix } from './cookies';
import { callAzureFuncPublic } from './call-azure-function-auth';

import dayjs from 'dayjs';

// Dynamic imports for store, userSlice, and clearReduxStore to break the circular
// dependency cycle: callAzureFunctions -> store -> notificationSlice -> callAzureFunctions
//                   callAzureFunctions -> clear-redux-store -> store (same cycle)
const getStoreModules = async () => {
  const [storeModule, userSliceModule, clearReduxStoreModule] = await Promise.all([
    import('../store'),
    import('store/features/userSlice'),
    import('./clear-redux-store')
  ]);
  return {
    store: storeModule.store,
    setLoggedOut: userSliceModule.setLoggedOut,
    clearReduxStore: clearReduxStoreModule.default
  };
};

const functionConfig = {
  baseURL: import.meta.env.VITE_APP_AZ_FUNCTIONS_BASE_URI,
  withCredentials: true
};

// Create a shared axios instance for authenticated requests
const authAxios = axios.create(functionConfig);

// Flag to prevent multiple concurrent logout redirects
let isLoggingOut = false;

// Intercept 401 responses and force logout
authAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && !isLoggingOut) {
      isLoggingOut = true;

      try {
        await callAzureFuncPublic({ url: '/logout', method: 'post' });
      } catch {
        // Ignore logout errors
      }

      const { store, setLoggedOut, clearReduxStore } = await getStoreModules();
      store.dispatch(setLoggedOut());
      clearReduxStore();
      sessionStorage.clear();
      localStorage.setItem('isLoggedIn', 'false');
      window.location.assign('/login');
    }

    return Promise.reject(error);
  }
);

// Shared promise to prevent concurrent refresh requests
let refreshPromise = null;

// Exported for other credentialed API clients (e.g. the streaming API in
// views/stream/streamApi.js) that authenticate with the same split-cookie JWT.
// eslint-disable-next-line consistent-return
const refreshAccessToken = async (accessToken) => {
  // If a refresh is already in progress, wait for it to complete
  if (refreshPromise) {
    return refreshPromise;
  }

  try {
    if (accessToken) {
      // decode token
      const decoded = jwtDecode(accessToken);

      if (decoded?.exp) {
        const expDate = new Date(decoded.exp * 1000);

        // check if token is close to expiration
        if (dayjs(expDate).diff(dayjs(), 'minute') <= 1) {
          // Store the refresh promise to prevent concurrent refreshes
          refreshPromise = callAzureFuncPublic({
            url: `/auth-refresh-access`,
            method: 'get'
          })
            .then((response) => {
              // Clear the promise on success
              refreshPromise = null;
              return response;
            })
            .catch((error) => {
              // Clear the promise on error
              refreshPromise = null;
              throw error;
            });

          // Wait for the refresh to complete
          await refreshPromise;
        }
      }
    } else {
      throw new Error('No access token');
    }
  } catch (error) {
    // Clear the promise in case of error
    refreshPromise = null;

    // Call logout endpoint BEFORE redirecting
    try {
      await callAzureFuncPublic({
        url: `/logout`,
        method: 'post'
      });
    } catch (logoutError) {
      // Ignore logout errors, proceed with cleanup
      console.error('Logout request failed:', logoutError);
    }

    // Then clear state and redirect
    const { store, setLoggedOut, clearReduxStore } = await getStoreModules();
    store.dispatch(setLoggedOut());
    clearReduxStore();
    sessionStorage.clear();
    localStorage.setItem('isLoggedIn', 'false');
    window.location.assign('/login');
  }
};

const callAzureFunction = async (requestConfig) => {
  const cookies = new Cookies(null, { path: '/' });

  // get cookie name suffix
  const cookieNameSuffix = getCookieNameSuffix();

  // get readable access token for functions
  const accessToken = cookies.get(`AF_ATHP${cookieNameSuffix}`);

  await refreshAccessToken(accessToken);

  const axiosResponse = await authAxios({
    ...requestConfig
  });

  return axiosResponse;
};

export { refreshAccessToken };
export default callAzureFunction;
